import { Facebook, Instagram, MapPin, Phone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import HashLink from './HashLink';
import { LOGO_URL } from '../mock';
import { useI18n } from '../contexts/I18nContext';

const SOCIALS = [
  { Icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61588942857783', label: 'Facebook' },
  { Icon: Instagram, href: 'https://www.instagram.com/gemsatthearts', label: 'Instagram' },
];

export default function Footer() {
  const { t } = useI18n();

  const offerLinks = [
    { label: t('footer.offer.arts'), href: '/#programs', hash: true },
    { label: t('footer.offer.enrichment'), href: '/summer-series', hash: false },
    { label: t('footer.offer.community'), href: '/#programs', hash: true },
    { label: t('footer.offer.space'), href: '/rentals', hash: false },
  ];
  const involvedLinks = [
    { label: t('footer.involved.volunteer'), href: '/get-involved', hash: false },
    { label: t('footer.involved.donate'), href: '/donate', hash: false },
    { label: t('footer.involved.vendor'), href: '/get-involved', hash: false },
    { label: t('footer.involved.sponsor'), href: '/donate', hash: false },
    { label: t('footer.involved.partner'), href: '/get-involved', hash: false },
  ];

  return (
    <footer className="bg-[#E5614C] text-white pt-20 pb-8 relative overflow-hidden">
      <div className="absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full bg-white/8 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-[#2A1B1A]/12 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 md:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Link to="/" className="inline-flex items-center">
              <div className="bg-white rounded-2xl p-2.5 shadow-[0_8px_20px_-6px_rgba(0,0,0,0.25)]">
                <img
                  src={LOGO_URL}
                  alt="GEMS @ The Arts"
                  className="h-14 lg:h-16 w-auto block"
                />
              </div>
            </Link>
            <p className="mt-5 text-white/85 text-[15px] leading-relaxed max-w-md">
              {t('footer.tagline')}
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="h-10 w-10 rounded-full bg-white/15 border border-white/25 hover:bg-white hover:text-[#E5614C] flex items-center justify-center transition-colors duration-300"
                  data-testid={`footer-social-${label.toLowerCase()}`}
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-semibold text-white text-[15px] mb-5">{t('footer.offer')}</h4>
            <ul className="space-y-3">
              {offerLinks.map((l) => (
                <li key={l.label}>
                  {l.hash ? (
                    <HashLink to={l.href} className="text-white/75 hover:text-white text-[14px] transition-colors cursor-pointer">
                      {l.label}
                    </HashLink>
                  ) : (
                    <Link to={l.href} className="text-white/75 hover:text-white text-[14px] transition-colors">
                      {l.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2">
            <h4 className="font-semibold text-white text-[15px] mb-5">{t('footer.involved')}</h4>
            <ul className="space-y-3">
              {involvedLinks.map((l) => (
                <li key={l.label}>
                  <Link to={l.href} className="text-white/75 hover:text-white text-[14px] transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <h4 className="font-semibold text-white text-[15px] mb-5">{t('footer.contact')}</h4>
            <ul className="space-y-3 text-[14px] text-white/80">
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-white shrink-0" />
                <span>160 Hollis Street<br />Framingham, MA 01702</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-white" />
                <a href="tel:5084052787" className="hover:text-white">(508) 405-2787</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-white" />
                <a href="mailto:info@gemsatthearts.org" className="hover:text-white">info@gemsatthearts.org</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/20 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="text-white/80 text-[13px]">
            &copy; 2026 GEMS @ The Arts. {t('footer.copyright')}
          </div>
          <div className="flex gap-6 text-[13px]">
            <a href="#" className="text-white/80 hover:text-white">{t('footer.privacy')}</a>
            <a href="#" className="text-white/80 hover:text-white">{t('footer.terms')}</a>
            <a href="#" className="text-white/80 hover:text-white">{t('footer.accessibility')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
